import EditIcon from '@app/assets/svg/EditIcon';
import InputText from '@app/sharedComponents/TextInput/TextInput';
import { Theme, themeTextVariants, ThemedText, ThemedView } from '@app/theme';
import { formatIndianCurrency } from '@app/utils/utility';
import { useTheme } from '@shopify/restyle';
import React, { useCallback, useMemo, useRef, useState } from 'react';
import {
  Dimensions,
  Platform,
  StyleSheet,
  TextInput as RNTextInput,
  TextStyle,
  TouchableOpacity,
} from 'react-native';
import { LimitConfig } from '../../types';

const AMOUNT_CHAR_WIDTH = 15.5;
const AMOUNT_WIDTH_PADDING = 12;

/** Hidden text used purely to measure the rendered width of the amount. */
const measureStyle = StyleSheet.create({
  amountMeasure: {
    position: 'absolute',
    opacity: 0,
    pointerEvents: 'none',
    fontSize: themeTextVariants.h1b.fontSize,
    lineHeight: themeTextVariants.h1b.lineHeight,
    fontWeight: '700' as TextStyle['fontWeight'],
    fontFamily: themeTextVariants.h1b.fontFamily,
  },
});

export type CreditLimitAmountInputProps = {
  limitConfig: LimitConfig;
  amountInput: string;
  setAmountInput: React.Dispatch<React.SetStateAction<string>>;
  applyAmount: (value: number, syncInput?: boolean) => number;
  isEditingInput: React.MutableRefObject<boolean>;
  marginBottom?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | '2xl';
  /** Fired when the edit (pencil) icon is tapped. */
  onEditPress?: () => void;
  /** Fired when an edited amount is committed (on blur), with the value. */
  onAmountConfirm?: (value: number) => void;
};

const CreditLimitAmountInput = ({
  limitConfig,
  amountInput,
  setAmountInput,
  applyAmount,
  isEditingInput,
  marginBottom = 'lg',
  onEditPress,
  onAmountConfirm,
}: CreditLimitAmountInputProps) => {
  const { colors } = useTheme<Theme>();
  const amountInputRef = useRef<RNTextInput>(null);
  const maxDigitLength = String(limitConfig.maxAmount).length + 1;

  const amountDisplayValue = useMemo(() => {
    if (!amountInput) return '';
    const value = parseInt(amountInput, 10);
    if (Number.isNaN(value)) return '';
    const formatted = formatIndianCurrency(value);
    return typeof formatted === 'string' ? formatted : String(value);
  }, [amountInput]);

  /** ₹ is part of the TextInput value so behaviour matches on web, iOS, and Android. */
  const amountFieldValue = amountDisplayValue
    ? `₹${amountDisplayValue}`
    : '₹';

  const measureText = amountFieldValue === '₹' ? '₹0' : amountFieldValue;

  const [measuredTextWidth, setMeasuredTextWidth] = useState(0);

  const amountInputWidth = useMemo(() => {
    const maxW = Dimensions.get('window').width - 72;
    const estimate = Math.ceil(measureText.length * AMOUNT_CHAR_WIDTH) + 24;
    const measured =
      measuredTextWidth > 0 ? measuredTextWidth + AMOUNT_WIDTH_PADDING : 0;
    return Math.min(Math.max(48, estimate, measured), maxW);
  }, [measureText, measuredTextWidth]);

  const onAmountMeasureLayout = useCallback(
    (e: { nativeEvent: { layout: { width: number } } }) => {
      const w = Math.ceil(e.nativeEvent.layout.width);
      if (w <= 0) return;
      setMeasuredTextWidth((prev) => (prev === w ? prev : w));
    },
    [],
  );

  const amountFieldInputStyle = useMemo(
    () => ({
      fontSize: themeTextVariants.h1b.fontSize,
      lineHeight: themeTextVariants.h1b.lineHeight,
      height: themeTextVariants.h1b.lineHeight,
      fontWeight: '700' as TextStyle['fontWeight'],
      fontFamily: themeTextVariants.h1b.fontFamily,
      color: colors['text-primary'],
      padding: 0,
      margin: 0,
      flex: 0,
      width: amountInputWidth,
      textAlign: 'center' as TextStyle['textAlign'],
      ...(Platform.OS === 'android'
        ? {
            includeFontPadding: false,
            textAlignVertical: 'center' as const,
          }
        : {}),
    }),
    [amountInputWidth, colors],
  );

  const handleAmountTextChange = useCallback(
    (text: string) => {
      isEditingInput.current = true;
      let numeric = text.replace(/[^0-9]/g, '');
      numeric = numeric.slice(0, maxDigitLength);
      setAmountInput(numeric);
      if (!numeric) return;

      const value = parseInt(numeric, 10);
      if (Number.isNaN(value)) return;
      applyAmount(value, false);
    },
    [applyAmount, isEditingInput, maxDigitLength, setAmountInput],
  );

  const handleAmountBlur = useCallback(() => {
    isEditingInput.current = false;
    const parsed = amountInput
      ? parseInt(amountInput, 10)
      : limitConfig.minAmount;
    const value = Number.isNaN(parsed) ? limitConfig.minAmount : parsed;
    applyAmount(value, true);
    onAmountConfirm?.(value);
  }, [amountInput, applyAmount, isEditingInput, limitConfig.minAmount, onAmountConfirm]);

  const handleAmountFocus = useCallback(() => {
    isEditingInput.current = true;
  }, [isEditingInput]);

  return (
    <ThemedView alignItems="center" mb={marginBottom} px="xs">
      <ThemedView
        alignSelf="center"
        style={{ maxWidth: '100%', position: 'relative' }}
      >
        <ThemedText
          variant="h1b"
          color="text-primary"
          style={measureStyle.amountMeasure}
          onLayout={onAmountMeasureLayout}
        >
          {measureText}
        </ThemedText>
        <InputText
          ref={amountInputRef}
          unstyled
          height={themeTextVariants.h1b.lineHeight}
          keyboardType="number-pad"
          value={amountFieldValue}
          onChangeText={handleAmountTextChange}
          onFocus={handleAmountFocus}
          onBlur={handleAmountBlur}
          showRightAccessory
          rightAccessory={() => (
            <TouchableOpacity
              hitSlop={{ left: 10, right: 10, bottom: 10, top: 10 }}
              onPress={() => {
                onEditPress?.();
                amountInputRef.current?.focus();
              }}
              style={{ marginLeft: 4, flexShrink: 0 }}
              accessibilityLabel="Edit credit limit amount"
            >
              <EditIcon size={20} color={colors['icon-action-primary']} />
            </TouchableOpacity>
          )}
          overRideContainerStyles={{
            alignSelf: 'center',
            maxWidth: '100%',
          }}
          overRideInputStyles={amountFieldInputStyle}
        />
      </ThemedView>
    </ThemedView>
  );
};

export default CreditLimitAmountInput;
